import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useAuth } from '@clerk/react'
import { apiClient } from './api-client'
import { TOKEN_REFRESH_INTERVAL_MS } from './constants'

interface ApiContextValue {
  token: string | undefined
  apiBase: string
  isLoading: boolean
  /** True once Clerk auth is loaded AND token has been set (or determined to be unavailable) */
  isReady: boolean
  isSignedIn: boolean
}

const ApiContext = createContext<ApiContextValue | undefined>(undefined)

export const ApiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getToken, isLoaded, isSignedIn } = useAuth()
  const [token, setToken] = useState<string | undefined>(undefined)
  const [isReady, setIsReady] = useState(false)

  // Token refresher for api-client to call on 401 errors
  const refreshToken = useCallback(async (): Promise<string | null> => {
    if (!isLoaded || !isSignedIn) return null
    try {
      const newToken = await getToken({ skipCache: true })
      if (newToken) {
        setToken(newToken)
        apiClient.setToken(newToken)
        return newToken
      }
    } catch {
      // Token refresh failed
    }
    return null
  }, [getToken, isLoaded, isSignedIn])

  // Register the token refresher with api-client
  useEffect(() => {
    apiClient.setTokenRefresher(refreshToken)
    return () => apiClient.setTokenRefresher(null)
  }, [refreshToken])

  useEffect(() => {
    const updateToken = async () => {
      if (isLoaded && isSignedIn) {
        try {
          const newToken = await getToken()
          setToken(newToken ?? undefined)
          apiClient.setToken(newToken ?? undefined)
        } catch {
          setToken(undefined)
          apiClient.setToken(undefined)
        }
      } else if (isLoaded) {
        // Loaded but not signed in
        setToken(undefined)
        apiClient.setToken(undefined)
      }
      // Mark as ready once we've processed the auth state
      if (isLoaded) {
        setIsReady(true)
      }
    }

    void updateToken()

    // Refresh token periodically (stays ahead of 60s expiry)
    const interval = setInterval(() => {
      void updateToken()
    }, TOKEN_REFRESH_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [getToken, isLoaded, isSignedIn])

  return (
    <ApiContext.Provider
      value={{
        token,
        apiBase: apiClient.apiBase,
        isLoading: !isLoaded,
        isReady,
        isSignedIn: isLoaded && isSignedIn
      }}
    >
      {children}
    </ApiContext.Provider>
  )
}

export const useApiContext = () => {
  const ctx = useContext(ApiContext)
  if (!ctx) throw new Error('useApiContext must be used within ApiProvider')
  return ctx
}
